import { useQuery } from '@tanstack/react-query'

import { makeClient } from '../api/client'
import { useAuth } from '../auth/AuthContext'

export type RepeatPremisesRow = {
  premises_id: string
  postcode: string
  address_line_1: string
  visits: number
  distinct_job_types: number
}

export type EngineerFTFRow = {
  engineer_id: string
  display_name: string
  external_ref: string
  completed: number
  failed: number
  total_closed: number
  ftf_pct: number
}

export type JobTypeRepeatRow = {
  job_type_id: string
  code: string
  display_name: string
  total_jobs: number
  unique_premises: number
  repeat_pct: number
}

export type FTFAnalytics = {
  window_days: number
  repeat_premises: RepeatPremisesRow[]
  engineer_ftf: EngineerFTFRow[]
  job_type_repeat: JobTypeRepeatRow[]
  summary: {
    total_jobs: number
    completed_jobs: number
    overall_ftf_pct: number
    repeat_premises_count: number
  }
}

export function useFTFAnalytics(windowDays = 30) {
  const { accessToken } = useAuth()
  const client = makeClient(() => accessToken)
  return useQuery({
    queryKey: ['analytics', 'ftf', windowDays],
    queryFn: () => client.call<FTFAnalytics>(`/api/v1/analytics/ftf?window_days=${windowDays}`),
    staleTime: 60_000, // analytics refresh every minute tops — cheap caching
  })
}

// --- trends ----------------------------------------------------------------

export type DailyVolumeRow = {
  date: string
  jobs_total: number
  jobs_completed: number
  jobs_failed: number
  exceptions_raised: number
}

export type CategoryRow = {
  category: string
  total: number
  critical: number
  high: number
  warn: number
  info: number
}

export type PostcodeHotspotRow = {
  postcode: string
  open_count: number
  critical: number
  high: number
  warn: number
}

export type SeverityTrendRow = {
  date: string
  critical: number
  high: number
  warn: number
  info: number
}

export type TrendsAnalytics = {
  window_days: number
  daily_volume: DailyVolumeRow[]
  hourly_demand: number[] // length 24 (UTC)
  category_breakdown: CategoryRow[]
  postcode_hotspots: PostcodeHotspotRow[]
  severity_trend: SeverityTrendRow[]
  summary: {
    total_exceptions_raised: number
    total_exceptions_open: number
    mtta_hours: number
    acknowledged_pct: number
    peak_hour_utc: number
    peak_hour_count: number
  }
}

export function useTrendsAnalytics(windowDays = 30) {
  const { accessToken } = useAuth()
  const client = makeClient(() => accessToken)
  return useQuery({
    queryKey: ['analytics', 'trends', windowDays],
    queryFn: () => client.call<TrendsAnalytics>(`/api/v1/analytics/trends?window_days=${windowDays}`),
    staleTime: 60_000,
  })
}
